import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import StatsWidget from '@rio-cloud/rio-uikit/StatsWidget';
import StatsWidgetNumber from '@rio-cloud/rio-uikit/StatsWidgetNumber';
import StatsWidgets from '@rio-cloud/rio-uikit/StatsWidgets';
import StatsWidgetSpacer from '@rio-cloud/rio-uikit/StatsWidgetSpacer';

import { useGetVehicleWarningSummaryQuery } from '@/services/warningsApi';
import Icon from './Icon';
import { Criticality } from './utils/types';

interface SummaryBarProps {
    assetId: string;
    activeFilters: Criticality[];
    onFilterToggle: (criticality: Criticality) => void;
    onFilterReset: () => void;
    isExpanded: boolean;
}

const CountValue = ({ criticality, count }: { criticality: Criticality; count: number }) => (
    <span className="display-flex align-items-center gap-3">
        <Icon criticality={criticality} size={16} />
        <span className="h2 margin-0 text-medium">{count}</span>
    </span>
);

const CountLabel = ({ id }: { id: string }) => (
    <span className="text-size-10-xs text-size-16-ls text-color-darkest text-center white-space-nowrap">
        <FormattedMessage id={id} />
    </span>
);

const STATS_CONFIG = [
    {
        criticality: Criticality.SCHEDULE_SERVICE,
        labelId: 'intl-msg:vehicle-warnings.status.scheduleService',
        color: 'text-color-danger',
    },
    {
        criticality: Criticality.SERVICE_SOON,
        labelId: 'intl-msg:vehicle-warnings.status.serviceSoon',
        color: 'text-color-warning',
    },
    {
        criticality: Criticality.INFORMATION,
        labelId: 'intl-msg:vehicle-warnings.status.information',
        color: 'text-color-info',
    },
];

const SummaryBar = ({ assetId, activeFilters, onFilterToggle, onFilterReset, isExpanded }: SummaryBarProps) => {
    const { data: summary } = useGetVehicleWarningSummaryQuery({ assetId });

    const hasFilter = activeFilters.length > 0;
    const hasBorder = !isExpanded ? 'rounded-none border-left-none border-right-none' : '';

    const counts: Record<Criticality, number> = {
        [Criticality.SCHEDULE_SERVICE]: summary?.schedule_service ?? 0,
        [Criticality.SERVICE_SOON]: summary?.service_soon ?? 0,
        [Criticality.INFORMATION]: summary?.information ?? 0,
    };

    const isDisabled = (count: number) => count === 0;
    const isFilteredOut = (criticality: Criticality) => hasFilter && !activeFilters.includes(criticality);
    const getOpacity = (criticality: Criticality, count: number) =>
        isDisabled(count) || isFilteredOut(criticality) ? 'opacity-40' : '';

    return (
        <StatsWidgets>
            <StatsWidget
                className={`margin-bottom-0 border border-color-light ${hasBorder}`}
                hasFilter={hasFilter}
                onFilterReset={onFilterReset}
            >
                <StatsWidget.Body>
                    {STATS_CONFIG.map(({ criticality, labelId, color }, index) => {
                        const count = counts[criticality];
                        return (
                            <Fragment key={criticality}>
                                {index > 0 && <StatsWidgetSpacer />}
                                <StatsWidgetNumber
                                    value={<CountValue criticality={criticality} count={count} />}
                                    label={<CountLabel id={labelId} />}
                                    className={`${color} ${getOpacity(criticality, count)}`}
                                    clickable={!isDisabled(count)}
                                    onClick={!isDisabled(count) ? () => onFilterToggle(criticality) : undefined}
                                />
                            </Fragment>
                        );
                    })}
                </StatsWidget.Body>
            </StatsWidget>
        </StatsWidgets>
    );
};

export default SummaryBar;
